"use strict";
/**
 * Shared AI Use Case domain types and helpers.
 *
 * Mirrors the portal's `Deliver/ai-core` module so the library can stay
 * independent of any host-application module layout. Hosts that already
 * normalise use cases on their side can simply pass instances of
 * `WsDeliverAiUseCase` to library components.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUseCaseStage = exports.normalizeAiUseCase = void 0;
var normalizeFields = function (fields) {
    return Object.keys(fields || {}).reduce(function (result, key) {
        var value = fields ? fields[key] : undefined;
        if (value !== null && value !== undefined) {
            result[key] = value;
        }
        return result;
    }, {});
};
var toNumeric = function (value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        var match = value.match(/-?\d+(?:[.,]\d+)?/);
        if (!match) {
            return null;
        }
        var parsed = Number.parseFloat(match[0].replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};
var toCategoricalPercent = function (value) {
    if (typeof value !== 'string') {
        return null;
    }
    var normalized = value.trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    if (normalized.includes('very high') || normalized.includes('critical')) {
        return 90;
    }
    if (normalized.includes('high')) {
        return 75;
    }
    if (normalized.includes('medium') || normalized.includes('moderate')) {
        return 50;
    }
    if (normalized.includes('very low')) {
        return 10;
    }
    if (normalized.includes('low')) {
        return 25;
    }
    return null;
};
var mapPriorityScoresToPrioritisationField = function (scores) {
    if (!Array.isArray(scores) || scores.length === 0) {
        return null;
    }
    var mapped = scores.reduce(function (result, item) {
        if (typeof (item === null || item === void 0 ? void 0 : item.strategyId) !== 'number') {
            return result;
        }
        if (typeof item.score !== 'number' || !Number.isFinite(item.score)) {
            return result;
        }
        result[String(item.strategyId)] = item.score;
        return result;
    }, {});
    return Object.keys(mapped).length > 0 ? mapped : null;
};
/**
 * Adapt an API record to the library model, keeping the original
 * `title`, `description`, `workflow`, and `systemInformation` properties.
 */
exports.normalizeAiUseCase = function (useCase) {
    var _a, _b, _c, _d, _e;
    var normalizedFields = normalizeFields(useCase.fields);
    var apiUseCase = useCase;
    // Compatibility for list payload values now returned at top level.
    if (!normalizedFields.Owner &&
        typeof apiUseCase.ownerName === 'string' &&
        apiUseCase.ownerName.trim()) {
        normalizedFields.Owner = apiUseCase.ownerName;
    }
    if (!normalizedFields.ClientGroup &&
        typeof apiUseCase.clientGroup === 'string' &&
        apiUseCase.clientGroup.trim()) {
        normalizedFields.ClientGroup = apiUseCase.clientGroup;
    }
    if (!normalizedFields.Area &&
        typeof apiUseCase.businessFunction === 'string' &&
        apiUseCase.businessFunction.trim()) {
        normalizedFields.Area = apiUseCase.businessFunction;
    }
    if (!normalizedFields.OwnerEmailAddress &&
        typeof apiUseCase.ownerEmailAddress === 'string' &&
        apiUseCase.ownerEmailAddress.trim()) {
        normalizedFields.OwnerEmailAddress = apiUseCase.ownerEmailAddress;
    }
    if (!normalizedFields.OwnerEmail &&
        typeof apiUseCase.ownerEmail === 'string' &&
        apiUseCase.ownerEmail.trim()) {
        normalizedFields.OwnerEmail = apiUseCase.ownerEmail;
    }
    // Compatibility layer for the new use-case endpoints that return
    // `priorityScores` at top level instead of `fields.Prioritisation`.
    if (!normalizedFields.Prioritisation) {
        var prioritisation = mapPriorityScoresToPrioritisationField(apiUseCase.priorityScores);
        if (prioritisation) {
            normalizedFields.Prioritisation = JSON.stringify(prioritisation);
        }
    }
    // Compatibility layer for impact values now returned at top level.
    if (!normalizedFields.impactScore && !normalizedFields.Impact && !normalizedFields.impact) {
        var impactValue = (_e = (_d = (_c = (_b = (_a = toNumeric(apiUseCase.impactScore)) !== null && _a !== void 0 ? _a : toNumeric(apiUseCase.impact)) !== null && _b !== void 0 ? _b : toNumeric(apiUseCase.businessFactor)) !== null && _c !== void 0 ? _c : toCategoricalPercent(apiUseCase.impactScore)) !== null && _d !== void 0 ? _d : toCategoricalPercent(apiUseCase.impact)) !== null && _e !== void 0 ? _e : toCategoricalPercent(apiUseCase.businessFactor);
        if (impactValue !== null) {
            normalizedFields.impactScore = impactValue;
        }
    }
    var normalized = {
        id: "" + (useCase.id != null ? useCase.id : ''),
        title: "" + (useCase.title || ''),
        fields: normalizedFields,
    };
    if (useCase.description) {
        normalized.description = "" + useCase.description;
    }
    if (useCase.workflow) {
        normalized.workflow = "" + useCase.workflow;
    }
    if (typeof apiUseCase.ownerName === 'string' && apiUseCase.ownerName.trim()) {
        normalized.ownerName = apiUseCase.ownerName;
    }
    if (typeof apiUseCase.ownerEmail === 'string' && apiUseCase.ownerEmail.trim()) {
        normalized.ownerEmail = apiUseCase.ownerEmail;
    }
    if (typeof apiUseCase.ownerEmailAddress === 'string' && apiUseCase.ownerEmailAddress.trim()) {
        normalized.ownerEmailAddress = apiUseCase.ownerEmailAddress;
    }
    if (typeof apiUseCase.clientGroup === 'string' && apiUseCase.clientGroup.trim()) {
        normalized.clientGroup = apiUseCase.clientGroup;
    }
    if (typeof apiUseCase.businessFunction === 'string' && apiUseCase.businessFunction.trim()) {
        normalized.businessFunction = apiUseCase.businessFunction;
    }
    if (typeof apiUseCase.area === 'string' && apiUseCase.area.trim()) {
        normalized.area = apiUseCase.area;
    }
    if (typeof apiUseCase.sourceSystem === 'string' && apiUseCase.sourceSystem.trim()) {
        normalized.sourceSystem = apiUseCase.sourceSystem;
    }
    if (typeof apiUseCase.stage === 'string' && apiUseCase.stage.trim()) {
        normalized.stage = apiUseCase.stage;
    }
    if (typeof useCase.readinessScore === 'number' && Number.isFinite(useCase.readinessScore)) {
        normalized.readinessScore = useCase.readinessScore;
    }
    if (useCase.systemInformation) {
        normalized.systemInformation = useCase.systemInformation;
    }
    return normalized;
};
/**
 * Maps known use-case ids to their workflow stage. Defaults to a
 * heuristic for ids that are not explicitly mapped.
 */
exports.getUseCaseStage = function (id) {
    if (id === '4') {
        return 4;
    }
    else if (id === '7') {
        return 4;
    }
    else if (id === '19') {
        return 3;
    }
    else if (id === '31') {
        return 3;
    }
    else if (id === '32') {
        return 3;
    }
    else if (id === '39') {
        return 7;
    }
    else if (id === '41') {
        return 4;
    }
    else if (id === '48') {
        return 3;
    }
    else if (id === '53') {
        return 2;
    }
    else if (id === '60') {
        return 2;
    }
    else if (id === '62') {
        return 6;
    }
    if (id < '50') {
        return 2;
    }
    return 1;
};
